"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getCompetitionTypeInitial, getCompetitionTypeLabel } from "./utils";
import type { CompetitionListItem } from "../types";
interface CompetitionItemProps {
  competition: CompetitionListItem;
  href: string;
}

/**
 * CompetitionItem — Horizontal layout matching the teams list pattern.
 * Logo on the left, competition name + sub-links + type badge on the right.
 */
export function CompetitionItem(t0) {
  const $ = _c(45);
  const {
    competition,
    href
  } = t0;
  const labels = useLabels();
  let t1;
  if ($[0] !== competition.type || $[1] !== labels) {
    t1 = getCompetitionTypeInitial(competition.type, labels);
    $[0] = competition.type;
    $[1] = labels;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const typeInitial = t1;
  let t2;
  if ($[3] !== competition.type || $[4] !== labels) {
    t2 = getCompetitionTypeLabel(competition.type, labels);
    $[3] = competition.type;
    $[4] = labels;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const typeFullName = t2;
  const t3 = competition.logo || "https://football.fansunitedassets.com/default/no-competition.png";
  let t4;
  if ($[6] !== competition.name || $[7] !== t3) {
    t4 = <Image src={t3} alt={competition.name} width={48} height={48} className="w-12 h-12 rounded-md object-contain" />;
    $[6] = competition.name;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== href || $[10] !== t4) {
    t5 = <Link href={href} prefetch={false} className="shrink-0">{t4}</Link>;
    $[9] = href;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== competition.name) {
    t6 = <span className="truncate">{competition.name}</span>;
    $[12] = competition.name;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronRight className="w-4 h-4 shrink-0" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== href || $[16] !== t6) {
    t8 = <Link href={href} prefetch={false} className="flex items-center gap-1 font-bold link-neutral hover:underline">{t6}{t7}</Link>;
    $[15] = href;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== typeFullName || $[19] !== typeInitial) {
    t9 = typeInitial && <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded bg-muted text-muted-foreground shrink-0 cursor-default" title={typeFullName}>{typeInitial}</span>;
    $[18] = typeFullName;
    $[19] = typeInitial;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="flex items-center gap-2">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = `${href}/standings`;
  let t12;
  if ($[24] !== labels.competitions.standings || $[25] !== t11) {
    t12 = <Link href={t11} prefetch={false} className="link-neutral hover:underline">{labels.competitions.standings}</Link>;
    $[24] = labels.competitions.standings;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-muted-foreground">·</span>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = `${href}/matches`;
  let t15;
  if ($[28] !== labels.competitions.matches || $[29] !== t14) {
    t15 = <Link href={t14} prefetch={false} className="link-neutral hover:underline">{labels.competitions.matches}</Link>;
    $[28] = labels.competitions.matches;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-muted-foreground">·</span>;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = `${href}/teams`;
  let t18;
  if ($[32] !== labels.competitions.teams || $[33] !== t17) {
    t18 = <Link href={t17} prefetch={false} className="link-neutral hover:underline">{labels.competitions.teams}</Link>;
    $[32] = labels.competitions.teams;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t12 || $[36] !== t15 || $[37] !== t18) {
    t19 = <div className="flex items-center gap-1 text-sm mt-1">{t12}{t13}{t15}{t16}{t18}</div>;
    $[35] = t12;
    $[36] = t15;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t10 || $[40] !== t19) {
    t20 = <div className="flex flex-col min-w-0">{t10}{t19}</div>;
    $[39] = t10;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20 || $[43] !== t5) {
    t21 = <div className="flex items-start gap-4 py-2">{t5}{t20}</div>;
    $[42] = t20;
    $[43] = t5;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
}